import type {
  SubscriptionDetails,
  V2BillingTier,
} from "schemas/generated/billing";

import React from "react";

import Coin from "@svg/billing/Coin";
import ProIconGlobe from "@svg/billing/Globe";
import PaperAirplane from "@svg/billing/PaperAirplane";
import Target from "@svg/billing/Target";

import { Check } from "lucide-react";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import {
  getV2BillingPlanInfoByTier,
  v2AdditionalShopProductUnitName,
} from "schemas/billing";

export const getCountOfV2AdditionalShopsInSubscription = (
  subscription: SubscriptionDetails,
) => {
  return subscription.productUnits.filter(
    (unit) => unit.name === v2AdditionalShopProductUnitName,
  ).length;
};

const getV2PlanIcon = (tier: V2BillingTier) => {
  switch (tier) {
    case "free":
      return <PaperAirplane />;
    case "starter":
      return <Coin />;
    case "pro":
      return <ProIconGlobe />;
    case "custom_v2":
      return <Target />;
    default:
      return null;
  }
};

export const V2PlanHeader = ({ tier }: { tier: V2BillingTier }) => {
  return (
    <div className="flex justify-start items-center gap-1">
      {getV2PlanIcon(tier)}
      <span className="font-editorial text-3xl font-normal">
        {getV2BillingPlanInfoByTier(tier).displayName}
      </span>
    </div>
  );
};

export const getV2PlanFeatures = (
  tier: V2BillingTier,
): { copy: React.ReactNode; icon: React.ReactNode }[] => {
  return exhaustiveSwitch({ type: tier })({
    free: () => [
      {
        copy: (
          <span>
            <span className="font-bold">100</span> messages/month
          </span>
        ),
        icon: <Check size={16} />,
      },
      {
        copy: "Replo subdomain only",
        icon: <Check size={16} />,
      },
      {
        copy: (
          <span>
            <span className="font-bold">Up to 10K</span> sessions/month
          </span>
        ),
        icon: <Check size={16} />,
      },
      { copy: "Email support", icon: <Check size={16} /> },
      {
        copy: "Replo badge on all pages",
        icon: <Check size={16} />,
      },
    ],
    starter: () => [
      {
        copy: (
          <span>
            <span className="font-bold">1000</span> messages/month
          </span>
        ),
        icon: <Check size={16} />,
      },
      {
        copy: "Connect to custom domains",
        icon: <Check size={16} />,
      },
      {
        copy: (
          <span>
            <span className="font-bold">Up to 100K</span> sessions/month
          </span>
        ),
        icon: <Check size={16} />,
      },
      { copy: "Email support", icon: <Check size={16} /> },
      {
        copy: "Remove Replo badge",
        icon: <Check size={16} />,
      },
    ],
    pro: () => [
      {
        copy: (
          <span>
            <span className="font-bold">Unlimited</span> messages/month
          </span>
        ),
        icon: <Check size={16} />,
      },
      {
        copy: (
          <span>
            Support for <span className="font-bold">up to 10 chats</span> at
            once
          </span>
        ),
        icon: <Check size={16} />,
      },
      {
        copy: (
          <span>
            <span className="font-bold">Up to 1M</span> sessions/month
          </span>
        ),
        icon: <Check size={16} />,
      },
      {
        copy: "Auto-generate custom landing pages from your ads or CSV uploads (Coming Soon)",
        icon: <Check size={16} />,
      },
      {
        copy: "Professional analytics and insights",
        icon: <Check size={16} />,
      },
    ],
    custom_v2: () => [
      {
        copy: (
          <span>
            <span className="font-bold">Priority support</span> and onboarding
            with a dedicated success manager
          </span>
        ),
        icon: <Check size={16} />,
      },
      {
        copy: (
          <span>
            <span className="font-bold">Managed services</span> for conversion
            rate optimization for any vertical
          </span>
        ),
        icon: <Check size={16} />,
      },
      {
        copy: (
          <span>
            <span className="font-bold">Custom integrations</span> with your
            stack
          </span>
        ),
        icon: <Check size={16} />,
      },
      {
        copy: (
          <span>
            <span className="font-bold">Guaranteed uptime SLA</span> (99.99%)
          </span>
        ),
        icon: <Check size={16} />,
      },
      {
        copy: (
          <span>
            <span className="font-bold">SSO & role-based</span> access control
            for teams
          </span>
        ),
        icon: <Check size={16} />,
      },
    ],
  });
};
